import { ConductorSurface } from "@faire/web-api--source/indigofair/data/ConductorSurface";

import { useConductorComponent } from "@faire/retailer-visitor-shared/features/Conductor/useConductorComponent";

import { mapConductorToPromoBanners, mapImpToPromoBanners, mapPromoBanners } from "./getBannerMappings";
import { usePromoBanners } from "@faire/retailer-visitor-shared/stores/ui/usePromoBanners";
import { PromoBannerType } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/bannerOrder";
import { FeatureName } from "@faire/web-api--source/indigofair/data/FeatureName";
import { BannerContent, BANNERS_SECTION_WITH_ORDERED_BANNERS_MAP, BannerSectionVariant, BannerSectionWithKeyAndContent } from "./siteBannersConsts";
import { useFOIBannerOrder } from "./useFOIBannerOrder";
import { useMemo } from "react";

/**
 * This hook is used to return the correct order of banners to be displayed, prioritizing the backend banners.
 * 1. Get eligible banners from backend (conductor and IMP).
 * 2. Get eligible banners from frontend.
 * 3. Narrow down rotation section of banners while keeping the content of the banners.
 * 4. Map resulting list of banners to FE banner to be rendered by MergedBanners.
 */
export const useSiteBannersList = (): BannerSectionWithKeyAndContent => {
  // conductor: only shows FOI banners for eligible retailers right now -> eventually will get all banners from here
  const { featureName, conductorSduiViewLayout } =
    useConductorComponent({ surface: ConductorSurface.SITE_WIDE_TOP_BANNER });

  // deprecated IMP banners and FE banners -> to be migrated to conductor
  const { inProductMessagingBanners, banners } = usePromoBanners();

  const conductorBannerTypes = mapConductorToPromoBanners(
    featureName ?? FeatureName.FEATURE_NAME_UNKNOWN,
    conductorSduiViewLayout
  );

  const impBannerTypes = mapImpToPromoBanners(inProductMessagingBanners);

  const promoBannerTypes = mapPromoBanners(banners);

  const foiBannerOrder = useFOIBannerOrder();

  // Inverse order, last insertion will override previous ones
  const mergedBanners = useMemo(() => new Map<PromoBannerType, BannerContent>([
    ...promoBannerTypes,
    ...impBannerTypes,
    ...conductorBannerTypes,
  ]), [promoBannerTypes, impBannerTypes, conductorBannerTypes]);

  return useMemo(() => getRotationSection(mergedBanners, foiBannerOrder), [mergedBanners, foiBannerOrder]);
};

const getRotationSection = (
  promoBanners: Map<PromoBannerType, BannerContent>,
  foiBannerOrder: PromoBannerType[]
): BannerSectionWithKeyAndContent => {
  // initialize mapping for each section with empty arrays
  const bannerSectionToPromoMapping: Record<BannerSectionVariant, BannerSectionWithKeyAndContent> = {
    [BannerSectionVariant.FAIRE_DIRECT_INCENTIVES]: [],
    [BannerSectionVariant.MARKET_COUNTDOWN]: [],
    [BannerSectionVariant.FIRST_ORDER_INCENTIVES]: [],
    [BannerSectionVariant.POST_FCO_BANNERS]: [],
    [BannerSectionVariant.ADDITIONAL_BANNERS]: [],
    [BannerSectionVariant.EMPLOYEE_GRANTED_FREE_FREIGHT]: [],
  };

  // Map banners to sections
  for (const [bannerType, bannerContent] of promoBanners) {
    for (const [section, sectionBannerTypes] of Object.entries(BANNERS_SECTION_WITH_ORDERED_BANNERS_MAP)) {
      if (sectionBannerTypes.includes(bannerType)) {
        bannerSectionToPromoMapping[section as unknown as BannerSectionVariant].push(
          {[bannerType]: bannerContent}
        );
      }
    }
  }

  // Return the first non-empty section
  for (const [section, eligibleSectionBannerTypes] of Object.entries(bannerSectionToPromoMapping)) {
    if (eligibleSectionBannerTypes.length === 0) {
      continue;
    }

    const shouldSkipPostFcoSection = 
      parseInt(section) === BannerSectionVariant.POST_FCO_BANNERS && 
      !eligibleSectionBannerTypes.some(banner => {
        const bannerType = Object.keys(banner)[0] as PromoBannerType;
        return bannerType === PromoBannerType.POST_FCO_INCENTIVE_DISCOUNT_JUNE_2024;
      });

    if (shouldSkipPostFcoSection) {
      continue;
    }

    // sort banners in section by priority
    const sectionBannersOrder = BANNERS_SECTION_WITH_ORDERED_BANNERS_MAP[section as unknown as BannerSectionVariant];
    const sortOrder = parseInt(section) === BannerSectionVariant.FIRST_ORDER_INCENTIVES ? foiBannerOrder : sectionBannersOrder;

    const eligibleBannersShown = eligibleSectionBannerTypes.filter((banner) => {
      const bannerType = Object.keys(banner)[0] as PromoBannerType;
      return sortOrder.includes(bannerType);
    });

    const sortedBanners = eligibleBannersShown.sort((a, b) => {
      const bannerA = Object.keys(a)[0] as PromoBannerType;
      const bannerB = Object.keys(b)[0] as PromoBannerType;
      return sortOrder.indexOf(bannerA) - sortOrder.indexOf(bannerB);
    });

    return sortedBanners;
  }
  return [];
};